import { FC, useEffect, useRef, useState } from "react";
import { cn } from "../../shared/lib/cn";
import { paintChalkBoard } from "./home-chalk-board-paint";
import {
  EMPTY_BOARD,
  findWinLine,
  getWinner,
  pickComputerMove,
  type TicBoard,
  type TicMark,
} from "./tic-tac-toe";

type GamePhase = "idle" | "player" | "thinking" | "ended";

const USER_MARK: TicMark = "X";
const AI_MARK: TicMark = "O";
const MARK_ANIM_MS = 1050;
const GRID_ANIM_MS = 800;
const WIN_MORPH_MS = 900;
const WIN_MORPH_DELAY_MS = 450;

/** Стартовая «завершённая» партия: O выиграла диагональ 2–4–6, последняя клетка — треугольник. */
const START_BOARD: TicBoard = [null, "X", "O", "X", "O", "O", "O", "X", "X"];
const START_WIN_MORPH_INDEX = 2;

function markName(cell: TicMark | null): string {
  if (cell === "X") return "Крестик";
  if (cell === "O") return "Нолик";
  return "Пусто";
}

function statusText(
  winner: ReturnType<typeof getWinner>,
  phase: GamePhase,
  roundStarted: boolean
): string {
  if (!roundStarted) return "";
  if (phase === "thinking") return "Думаю…";
  if (winner === "X") return "Вы выиграли";
  if (winner === "O") return "Нолики выиграли";
  if (winner === "draw") return "Ничья";
  if (phase === "player") return "Ваш ход — X";
  return "Крестики-нолики";
}

function resetLabel(roundStarted: boolean, gameOver: boolean): string {
  if (!roundStarted) return "Играть";
  if (!gameOver) return "Игра";
  return "Сыграть ещё";
}

function findNewMarkIndex(prev: TicBoard, next: TicBoard): number | null {
  for (let index = 0; index < next.length; index += 1) {
    if (prev[index] === null && next[index] !== null) return index;
  }
  return null;
}

export const HomeChalkTicTacToe: FC = () => {
  const [board, setBoard] = useState<TicBoard>(START_BOARD);
  const [phase, setPhase] = useState<GamePhase>("ended");
  const [winMorphIndex, setWinMorphIndex] = useState<number | null>(START_WIN_MORPH_INDEX);
  const [roundStarted, setRoundStarted] = useState(false);

  const boardRef = useRef<TicBoard>(START_BOARD);
  const prevBoardRef = useRef<TicBoard>(START_BOARD);
  const boardWrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const timerRef = useRef<number | null>(null);
  const morphTimerRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const gridIntroDoneRef = useRef(false);
  const winMorphIndexRef = useRef<number | null>(START_WIN_MORPH_INDEX);
  const winMorphRef = useRef(1);
  const skipFirstBoardPaintRef = useRef(true);
  const triangleSpinRef = useRef(0);
  const spinRafRef = useRef<number | null>(null);

  boardRef.current = board;

  const winner = getWinner(board);
  const gameOver = winner !== null;

  const stopAnimation = () => {
    if (rafRef.current !== null) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  const paintBoard = (
    snapshot: TicBoard,
    animatingIndex: number | null = null,
    animProgress = 1,
    gridProgress = 1
  ) => {
    const canvas = canvasRef.current;
    const wrap = boardWrapRef.current;
    if (!canvas || !wrap) return;

    paintChalkBoard(canvas, wrap, {
      board: snapshot,
      animatingIndex,
      animProgress,
      gridProgress,
      winMorphIndex: winMorphIndexRef.current,
      winMorphProgress: winMorphRef.current,
      triangleSpin: triangleSpinRef.current,
    });
  };

  const paintStatic = (snapshot: TicBoard, gridProgress = 1) => {
    paintBoard(snapshot, null, 1, gridProgress);
  };

  const scheduleWinMorph = () => {
    if (morphTimerRef.current !== null) return;
    if (winMorphIndexRef.current === null || getWinner(boardRef.current) === "draw") return;
    if (winMorphRef.current >= 1) return;

    morphTimerRef.current = window.setTimeout(() => {
      morphTimerRef.current = null;
      runWinMorph();
    }, WIN_MORPH_DELAY_MS);
  };

  const runWinMorph = () => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReducedMotion) {
      winMorphRef.current = 1;
      paintStatic(boardRef.current, 1);
      return;
    }

    stopAnimation();
    const start = performance.now();

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / WIN_MORPH_MS);
      winMorphRef.current = t;
      paintStatic(boardRef.current, 1);

      if (t < 1) {
        rafRef.current = window.requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
      }
    };

    rafRef.current = window.requestAnimationFrame(tick);
  };

  const afterWinPaint = (snapshot: TicBoard) => {
    if (winMorphIndexRef.current !== null && getWinner(snapshot) !== "draw") {
      scheduleWinMorph();
    }
  };

  const runMarkAnimation = (
    snapshot: TicBoard,
    animatingIndex: number,
    onDone?: () => void
  ) => {
    stopAnimation();
    const start = performance.now();

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / MARK_ANIM_MS);
      paintBoard(snapshot, animatingIndex, t, 1);

      if (t < 1) {
        rafRef.current = window.requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
        paintStatic(snapshot, 1);
        onDone?.();
      }
    };

    rafRef.current = window.requestAnimationFrame(tick);
  };

  const runIntroAnimation = (
    snapshot: TicBoard,
    newMarkIndex: number | null,
    onDone?: () => void
  ) => {
    stopAnimation();
    const start = performance.now();

    const tick = (now: number) => {
      const elapsed = now - start;
      const gridT = Math.min(1, elapsed / GRID_ANIM_MS);
      const markT = newMarkIndex === null ? 1 : Math.min(1, elapsed / MARK_ANIM_MS);

      paintBoard(snapshot, newMarkIndex, markT, gridT);

      const gridDone = gridT >= 1;
      const markDone = newMarkIndex === null || markT >= 1;

      if (!gridDone || !markDone) {
        rafRef.current = window.requestAnimationFrame(tick);
        return;
      }

      rafRef.current = null;
      paintStatic(snapshot, 1);
      onDone?.();
    };

    rafRef.current = window.requestAnimationFrame(tick);
  };

  const scheduleBoardPaint = (snapshot: TicBoard, prev: TicBoard) => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const newMarkIndex = findNewMarkIndex(prev, snapshot);

    if (!gridIntroDoneRef.current) {
      if (prefersReducedMotion) {
        paintStatic(snapshot, 1);
        gridIntroDoneRef.current = true;
        afterWinPaint(snapshot);
        return;
      }

      runIntroAnimation(snapshot, newMarkIndex, () => {
        gridIntroDoneRef.current = true;
        afterWinPaint(snapshot);
      });
      return;
    }

    if (newMarkIndex !== null && !prefersReducedMotion) {
      runMarkAnimation(snapshot, newMarkIndex, () => afterWinPaint(snapshot));
      return;
    }

    paintStatic(snapshot, 1);
    afterWinPaint(snapshot);
  };

  const syncWinState = (snapshot: TicBoard, lastMarkIndex: number | null) => {
    const line = findWinLine(snapshot);

    if (!line) {
      winMorphIndexRef.current = null;
      winMorphRef.current = 0;
      setWinMorphIndex(null);
      return;
    }

    const currentMorph = winMorphIndexRef.current;
    const morphIndex =
      lastMarkIndex !== null && line.includes(lastMarkIndex)
        ? lastMarkIndex
        : currentMorph !== null && line.includes(currentMorph)
          ? currentMorph
          : line[line.length - 1];

    winMorphIndexRef.current = morphIndex;
    setWinMorphIndex(morphIndex);
    setPhase("ended");
  };

  useEffect(() => {
    return () => {
      stopAnimation();
      stopTriangleSpin();
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      if (morphTimerRef.current !== null) window.clearTimeout(morphTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (skipFirstBoardPaintRef.current) {
      skipFirstBoardPaintRef.current = false;
      gridIntroDoneRef.current = true;
      winMorphIndexRef.current = START_WIN_MORPH_INDEX;
      winMorphRef.current = 1;
      setWinMorphIndex(START_WIN_MORPH_INDEX);
      setPhase("ended");
      paintStatic(START_BOARD, 1);
      prevBoardRef.current = START_BOARD;
      return;
    }

    const prev = prevBoardRef.current;
    const lastMarkIndex = findNewMarkIndex(prev, board);
    syncWinState(board, lastMarkIndex);
    scheduleBoardPaint(board, prev);
    prevBoardRef.current = board;
  }, [board]);

  useEffect(() => {
    const wrap = boardWrapRef.current;
    if (!wrap) return;

    const paintFinishedStart = () => {
      if (winMorphIndexRef.current === null && getWinner(boardRef.current) !== null) {
        winMorphIndexRef.current = START_WIN_MORPH_INDEX;
        winMorphRef.current = 1;
      }
      paintStatic(boardRef.current, gridIntroDoneRef.current ? 1 : 0);
    };

    paintFinishedStart();

    const observer = new ResizeObserver(() => {
      paintFinishedStart();
    });

    observer.observe(wrap);
    return () => observer.disconnect();
  }, []);

  const resetGame = () => {
    stopAnimation();
    stopTriangleSpin();
    triangleSpinRef.current = 0;
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    if (morphTimerRef.current !== null) window.clearTimeout(morphTimerRef.current);
    morphTimerRef.current = null;
    prevBoardRef.current = EMPTY_BOARD;
    gridIntroDoneRef.current = false;
    winMorphRef.current = 0;
    winMorphIndexRef.current = null;
    skipFirstBoardPaintRef.current = false;
    setBoard(EMPTY_BOARD);
    setWinMorphIndex(null);
    setPhase("player");
    setRoundStarted(true);
  };

  const playComputer = (nextBoard: TicBoard) => {
    setPhase("thinking");

    timerRef.current = window.setTimeout(() => {
      const move = pickComputerMove(nextBoard, AI_MARK);
      if (move < 0) return;

      setBoard((prev) => {
        const updated = [...prev];
        updated[move] = AI_MARK;
        return updated;
      });
      setPhase("player");
    }, 650);
  };

  const stopTriangleSpin = () => {
    if (spinRafRef.current === null) return;
    window.cancelAnimationFrame(spinRafRef.current);
    spinRafRef.current = null;
  };

  const startTriangleSpin = () => {
    if (spinRafRef.current !== null) return;
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) return;

    let last = performance.now();
    const tick = (now: number) => {
      const delta = now - last;
      last = now;
      triangleSpinRef.current += (delta / 1400) * Math.PI * 2;
      paintStatic(boardRef.current, 1);
      spinRafRef.current = window.requestAnimationFrame(tick);
    };

    spinRafRef.current = window.requestAnimationFrame(tick);
  };

  const handleCellClick = (index: number) => {
    if (winMorphIndex === index && gameOver) {
      resetGame();
      return;
    }

    if (phase !== "player" || board[index] !== null || gameOver) return;

    const nextBoard = [...board];
    nextBoard[index] = USER_MARK;
    setBoard(nextBoard);

    const nextWinner = getWinner(nextBoard);
    if (nextWinner !== null) return;

    playComputer(nextBoard);
  };

  const status = statusText(winner, phase, roundStarted);
  const canPlay = phase === "player" && !gameOver;
  const actionLabel = resetLabel(roundStarted, gameOver);
  const roundInProgress = roundStarted && !gameOver;

  return (
    <section className="home-chalk-game" aria-label="Крестики-нолики">
      {status && (
        <p className="home-chalk-game__status" role="status">
          {status}
        </p>
      )}

      <div
        ref={boardWrapRef}
        className={cn("home-chalk-game__board", gameOver && "home-chalk-game__board--ended")}
      >
        <canvas className="home-chalk-game__surface" ref={canvasRef} aria-hidden />
        <div className="home-chalk-game__hits">
          {board.map((cell, index) => {
            const isWinCell = winMorphIndex === index;
            const cellName = `Клетка ${index + 1}: ${markName(cell)}`;
            const restartsGame = isWinCell && gameOver;
            const cellAriaLabel = restartsGame ? `${cellName}. ${actionLabel}` : cellName;

            return (
              <button
                key={index}
                type="button"
                className={cn(
                  "home-chalk-game__cell",
                  isWinCell && "home-chalk-game__cell--win"
                )}
                onClick={() => handleCellClick(index)}
                onMouseEnter={isWinCell ? startTriangleSpin : undefined}
                onMouseLeave={isWinCell ? stopTriangleSpin : undefined}
                disabled={(!canPlay || cell !== null) && !isWinCell}
                aria-label={cellAriaLabel}
              />
            );
          })}
        </div>
      </div>

      <button
        type="button"
        className="home-chalk-game__reset"
        onClick={resetGame}
        disabled={roundInProgress}
      >
        {actionLabel}
      </button>
    </section>
  );
};
